import { Tag } from '@components/core-ui/Tag';
import { CheckBox } from '@components/core-ui/form-elements/CheckBox';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { IconButtonRegular } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonRegular';
import DownloadIcon from '@components/icons/Download.svg';
import { useEffect, useRef, useState } from 'react';
import type { AsrTranscript } from '@pages/Admin/AsrProxyConsolePage/api/types';
import { downloadTranscriptJson } from '@pages/Admin/AsrProxyConsolePage/helpers/downloadTranscriptJson';
import { formatTimestamp } from '@pages/Admin/AsrProxyConsolePage/helpers/formatTimestamp';
import { getStatusTagClassName } from '@pages/Admin/AsrProxyConsolePage/helpers/getStatusTagClassName';

const PAGE_SIZE = 50;

type TProps = {
  result: AsrTranscript | null;
  error: string;
  page: number;
  setPage: (page: number) => void;
};

export const ResultView = ({ result, error, page, setPage }: TProps) => {
  const [showJson, setShowJson] = useState(false);
  const transcriptRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    transcriptRef.current?.scrollTo({ top: 0 });
  }, [page]);

  if (error) {
    return <div className="whitespace-pre-wrap text-sm text-[var(--color-danger)]">{error}</div>;
  }

  if (!result) {
    return <p className="text-sm text-foreground-muted">The normalized AssemblyAI-shaped transcript renders here.</p>;
  }

  const total = result.utterances.length;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const current = Math.min(page, pages - 1);
  const slice = result.utterances.slice(current * PAGE_SIZE, current * PAGE_SIZE + PAGE_SIZE);
  const isError = result.status === 'error';
  const jsonView = showJson || isError;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Tag text={result.status} className={getStatusTagClassName(result.status)} />
          <span className="text-xs uppercase tracking-wide text-foreground-muted">
            {result.provenance.provider} · {result.provenance.providerModel ?? result.provenance.model}
          </span>
          {result.multichannel ? <Tag text="multichannel" className="tag--muted-rounded" /> : null}
          {result.confidence != null ? (
            <span className="text-sm text-foreground-muted">conf {result.confidence.toFixed(2)}</span>
          ) : null}
          {total > 0 ? (
            <span className="text-xs uppercase tracking-wide text-foreground-muted">{total} utt</span>
          ) : null}
        </div>
        <div className="flex items-center gap-3">
          {!isError ? <CheckBox name="playground-json" label="JSON" checked={showJson} onChange={setShowJson} /> : null}
          <IconButtonRegular
            title="Download asr.json"
            aria-label="Download asr.json"
            onClick={() => downloadTranscriptJson(result)}
          >
            <DownloadIcon />
          </IconButtonRegular>
        </div>
      </div>

      {!jsonView && result.error ? (
        <div className="mb-3 whitespace-pre-wrap text-sm text-[var(--color-danger)]">{result.error}</div>
      ) : null}
      {result.provenance.fallbackReason ? (
        <div className="mb-3 whitespace-pre-wrap text-sm text-foreground-muted">
          Fell back after — {result.provenance.fallbackReason}
        </div>
      ) : null}

      {jsonView ? (
        <pre className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-border bg-page p-3 text-xs leading-relaxed text-foreground">
          {JSON.stringify(result, null, 2)}
        </pre>
      ) : (
        <div ref={transcriptRef} className="min-h-0 flex-1 overflow-y-auto">
          {slice.map((utterance, index) => (
            <div key={current * PAGE_SIZE + index} className="flex gap-3 border-b border-dashed border-border py-2">
              <span className="w-[42px] shrink-0 font-semibold text-accent">{utterance.speaker}</span>
              <span className="w-[70px] shrink-0 text-xs tabular-nums text-foreground-muted">
                {formatTimestamp(utterance.start)}
              </span>
              <span className="text-sm">{utterance.text}</span>
            </div>
          ))}
          {pages > 1 ? (
            <div className="mt-6 flex items-center justify-center gap-3">
              <OutlinedButton className="!w-auto" disabled={current === 0} onClick={() => setPage(current - 1)}>
                ‹ Prev
              </OutlinedButton>
              <span className="text-sm text-foreground-muted">
                Page {current + 1} / {pages}
              </span>
              <OutlinedButton className="!w-auto" disabled={current >= pages - 1} onClick={() => setPage(current + 1)}>
                Next ›
              </OutlinedButton>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
